import axios from 'axios';
import { ENV_CONFIG } from '../config/env.config';

// ─────────────────────────────────────────────────────────────────────────────
// File upload service — ported from the Angular EMIS `uploadLargeFile`.
//
// Multipart POST to the EC2 large-file upload endpoint:
//   POST `${largeFileUploadApi}`
//   FormData: { file, fileName, bucketName }
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Upload a file via the large-file (multipart) endpoint. Mirrors Angular
 * uploadLargeFile(file, fileName, bucketName).
 *
 * @param file        The File to upload.
 * @param fileName    Destination key/name (e.g. "student_exposure/images/x.jpg").
 * @param bucketName  Target S3 bucket.
 */
export async function uploadLargeFile(
  file: File,
  fileName: string,
  bucketName: string
): Promise<any> {
  const endpoint = ENV_CONFIG.largeFileUploadApi;
  if (!endpoint) {
    // Prevents a relative POST to the current page (which looks like a reload).
    throw new Error(
      'largeFileUploadApi is not configured. Restart the dev server after updating env.'
    );
  }

  const formData = new FormData();
  formData.append('file', file, file.name);
  formData.append('fileName', fileName);
  formData.append('bucketName', bucketName);

  // Attach the same auth headers the rest of the app sends (mirrors the
  // Angular HttpClient interceptor). Do NOT set Content-Type — the browser
  // sets the correct multipart boundary automatically.
  const authKey = localStorage.getItem('AuthorizationKey');
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {};
  if (authKey) headers.Authorization = authKey;
  if (token) headers.Token = token;

  const { data } = await axios.post(endpoint, formData, { headers });
  return data;
}

/**
 * Get a signed, viewable CDN URL for a stored file. Mirrors Angular
 * getUploadedFiles(bucketName, fileName, expiry).
 */
export async function getUploadedFileUrl(
  bucketName: string,
  fileName: string,
  expiry: number
): Promise<any> {
  const { data } = await axios.get(ENV_CONFIG.readingFileApi, {
    params: { bcktId: bucketName, filename: fileName, expiry },
  });
  return data;
}

// ─────────────────────────────────────────────────────────────────────────────
// DrRadhaFileupld — multipart upload on the emis1 (php1) service.
//   POST `${php1Url}/api/DrRadhaFileupld`
//   FormData: { file, folder_path }
//   Response: { dataStatus, data: { share_link, file_name } }
// ─────────────────────────────────────────────────────────────────────────────

export interface StuExposureUploadResult {
  dataStatus: boolean;
  message?: string;
  data?: { share_link?: string; file_name?: string; [k: string]: any };
  [k: string]: any;
}

export async function stuExposureDetUploads(
  file: File,
  folderPath: string
): Promise<StuExposureUploadResult> {
  const formData = new FormData();
  formData.append('file', file, file.name);
  formData.append('folder_path', folderPath);

  const authKey = localStorage.getItem('AuthorizationKey');
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {};
  if (authKey) headers.Authorization = authKey;
  if (token) headers.Token = token;

  const url = `${ENV_CONFIG.djangoEmisUrl.replace(/\/$/, '')}/StuExposureDetUploads`;
  const { data } = await axios.post(url, formData, { headers });
  return data;
}
